import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import useDate from '../../../hooks/useDate/useDate';
import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';

import { RootState } from '../../../stores/Store';
import { removeBulkFilter, resetBulkFilter } from '../../../stores/TransactionsStore';

import { Sort } from '../../../models/CommonModels';
import {
  TransactionList,
  TransactionsFilter,
  TransactionsListRequest,
  TransactionStatus,
  TransactionType,
} from '../../../models/TransactionsModels';

import { Header, Row } from '../../../components/Table/TableModels';
import TruncatedTooltip from '../../../components/TruncatedTooltip/TruncatedTooltip';
import Tooltip from '../../../components/Tooltip/Tooltip';
import FilterTag from '../../../components/FilterTag/FilterTag';
import { ClearAllTagsContainer } from '../../../components/FilterTag/FilterTagStyle';

import TransactionsService from '../../../services/Transactions/TransactionsService';
import { FileFormat } from '../../../models/ImportModels';

function useTransactionDetails(id: string | undefined, activePage: number, pageSize: number, sort: Sort) {
  const [t] = useTranslation();
  const snackBar = useSnackBar();
  const dispatch = useDispatch();
  const { localeDateFormat, localeDateTimeFormat } = useDate();
  const [isLoading, setIsLoading] = useState(false);
  const [transactionType, setTransactionType] = useState<TransactionType>();
  const [transactions, setTransactions] = useState<TransactionList[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const { bulkFilter } = useSelector((state: RootState) => state.transactionsReducer);
  /**
   * Fetches the device details by id
   */
  const fetchTransactionDetails = () => {
    setIsLoading(true);
    const requestParams: TransactionsListRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order,
      bulkId: id,
      filter: bulkFilter.filter,
      statusList: bulkFilter.status ? [bulkFilter.status] : undefined,
    };

    TransactionsService.api.fetchTransactions(requestParams).then(
      (response) => {
        setTransactions(response.data.transactions || []);
        setTransactionType(response.data.journey);
        setTotalItems(response.data.totalElements || 0);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.transactions.fetchTransactionsError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Exports transactions details
   */
  const exportData = (format: FileFormat) => {
    setIsLoading(true);
    const requestParams: TransactionsListRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order.toUpperCase(),
      bulkId: id,
      filter: bulkFilter.filter,
      journey: transactionType || undefined,
      statusList: bulkFilter.status ? [bulkFilter.status] : undefined,
    };

    TransactionsService.api
      .exportData(
        requestParams,
        format,
        `requests_${transactionType?.toLowerCase()}_${DateTime.now().toFormat(localeDateTimeFormat)}.${format}`,
      )
      .catch(() => {
        snackBar.showSnackBar(t('pages.transactions.exportFileError'), 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  React.useEffect(() => {
    if (id) {
      fetchTransactionDetails();
    }
  }, [id, activePage, pageSize, sort, bulkFilter]);

  /**
   * Removes the filter from the store
   * @param filterKey
   */
  const onRemoveFilter = (filterKey: keyof TransactionsFilter) => {
    dispatch(removeBulkFilter(filterKey));
  };

  /**
   * Applies the proper transformations to a given type of filter tag
   * @param filterKey
   */
  const filterTagAdapter = (filterKey: keyof TransactionsFilter) => {
    if (bulkFilter[filterKey] === '') return null;
    switch (filterKey) {
      case 'filter':
      case 'type':
        return null;
      case 'status':
        return (
          <FilterTag key={filterKey} onRemove={() => onRemoveFilter(filterKey)}>
            {t('pages.transactions.table.filters.status') +
              t(`pages.transactions.table.status.${bulkFilter[filterKey]}`)}
          </FilterTag>
        );
      default:
        return null;
    }
  };

  const renderFilterTags = useCallback(() => {
    const existingFilterKeys = Object.keys(bulkFilter);
    const filterTags: ReactNode[] = [];
    existingFilterKeys.forEach((fk) => {
      const f = filterTagAdapter(fk as keyof TransactionsFilter);
      if (f) {
        filterTags.push(f);
      }
    });

    if (filterTags.length > 0) {
      filterTags.push(
        <ClearAllTagsContainer key="clearAll" onClick={() => dispatch(resetBulkFilter())}>
          {t('common.clearAll')}
        </ClearAllTagsContainer>,
      );
    }

    return filterTags;
  }, [bulkFilter]);

  const headers: Header[] = useMemo(
    () =>
      ['creationDate', 'status', 'userFullName', 'userEmail', 'serialNumber', 'eid'].map((item) => ({
        id: item,
        label: t(`pages.transactions.table.headers.${item}`),
        isSortable: true,
      })),
    [t],
  );

  const rows: Row[] = useMemo(
    () =>
      transactions?.map((transaction) => ({
        id: transaction.actionId,
        cells: [
          {
            value: transaction.creationDate
              ? DateTime.fromISO(transaction.creationDate).toFormat(localeDateFormat)
              : '',
          },
          {
            value: transaction.status ? (
              <Tooltip
                label={t(`pages.transactions.table.status.${transaction.status}`)}
                message={
                  transaction.status === TransactionStatus.ERROR ? t(`pages.transactions.table.errorStatusTooltip`) : ''
                }
                sx={{ width: '12.125rem' }}
              />
            ) : (
              ''
            ),
          },
          {
            value: transaction.userFullName,
          },
          {
            value: transaction.userEmail,
          },
          {
            value: transaction.serialNumber ? <TruncatedTooltip label={transaction.serialNumber} limit={4} /> : '',
          },
          {
            value: transaction.eid ? <TruncatedTooltip label={transaction.eid} limit={4} /> : '',
          },
        ],
      })),
    [t, transactions],
  );

  return {
    isLoading,
    transactions,
    totalItems,
    transactionType,
    renderFilterTags,
    headers,
    rows,
    exportData,
  };
}

export default useTransactionDetails;
